import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TextInput, SaveButton, useDataProvider, useNotify, useRefresh, useTranslate, usePermissions } from 'react-admin';
import { Box, InputAdornment, IconButton, useMediaQuery, useTheme, Tooltip, CircularProgress, Button } from '@mui/material';
import { useWatch, useFormContext } from 'react-hook-form';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';
import { CustomRichText } from '@/components/global/inputs/CustomRichText';
import ArrowRightSmallIcon from '@/assets/icons/arrow-right-small-icon.svg?react';
import HiddenIcon from '@/assets/icons/eye-hidden-icon.svg?react';
import ShowIcon from '@/assets/icons/show-icon.svg?react';
import { activityStyles } from '@/themes/commonStyles/activity.js';
import CameraIcon from '@/assets/icons/camera-icon.svg?react';
import CommentImageThumbnailsContainer from './CommentImageThumbnailsContainer.jsx';
import CommentImageSlider from './CommentImageSlider.jsx';
import { getPlainTextFromHtml } from '@/helpers/format/getPlainTextFromHtml.js';
import { IMAGE_CONSTANTS } from '@/constants/ActivitiesConfig.js';
import { commentImageBlobCache } from '@/composables/useCommentImages.js';

const validateActivitiesLength = translate => value => {
    if (!value) return undefined;

    const plainText = getPlainTextFromHtml(value);
    const textWithoutWhitespace = plainText.replaceAll(/\s/g, '');
    const length = textWithoutWhitespace.length;

    if (length < 3) {
        return translate('common.validation.minLength', { count: 3 });
    }

    if (length > 10000) {
        return translate('common.validation.maxLength', { count: 10000 });
    }

    return undefined;
};

const generateUniqueId = (() => {
    let counter = 0;
    return () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        counter += 1;
        return `${Date.now()}-${counter}-${performance.now().toString(36).replace('.', '')}`;
    };
})();

const validateFile = file => {
    return IMAGE_CONSTANTS.ALLOWED_TYPES_SET.has(file.type) && file.size <= IMAGE_CONSTANTS.MAX_FILE_SIZE;
};

const createAttachmentFromFile = file => ({
    id: `${file.name}-${file.size}-${file.lastModified}-${generateUniqueId()}`,
    file,
    previewUrl: URL.createObjectURL(file),
});

const processFiles = (files, currentCount, notify, translate) => {
    if (!files.length) return { validFiles: [], shouldClear: false };

    if (currentCount >= IMAGE_CONSTANTS.MAX_FILES) {
        notify(translate('resources.serviceRequest.activityList.maxImagesReached'), {
            type: 'warning',
        });
        return { validFiles: [], shouldClear: true };
    }

    const remainingSlots = IMAGE_CONSTANTS.MAX_FILES - currentCount;
    const candidateFiles = files.slice(0, remainingSlots);

    const { validFiles, invalidFiles } = candidateFiles.reduce(
        (acc, file) => {
            if (validateFile(file)) {
                acc.validFiles.push(file);
            } else {
                acc.invalidFiles.push(file);
            }
            return acc;
        },
        { validFiles: [], invalidFiles: [] },
    );

    if (currentCount + files.length > IMAGE_CONSTANTS.MAX_FILES) {
        notify(translate('resources.serviceRequest.activityList.maxImagesReached'), {
            type: 'warning',
        });
    }

    if (invalidFiles.length) {
        notify(translate('resources.serviceRequest.activityList.invalidFiles'), { type: 'error' });
    }

    return { validFiles, shouldClear: !validFiles.length };
};

const CommentInput = ({
    source,
    serviceRequestId,
    serviceInspectionId,
    isEditingComment = false,
    changeId,
    onCancelEdit,
    onEditSuccess,
    existingImageBlobUrls = [],
    existingImageNames = [],
    isHidden,
    resource,
    autoOpen = false,
    onAutoOpenHandled,
}) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const { permissions } = usePermissions();
    const isAdmin = permissions.isAdmin;
    const isServiceMaster = permissions.isServiceMaster;
    const [isEditing, setIsEditing] = useState(isEditingComment);
    const { control, setValue, handleSubmit } = useFormContext();
    const activitiesValue = useWatch({ control, name: source });
    const editorRef = useRef(null);
    const isMountedRef = useRef(false);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const dataProvider = useDataProvider();
    const [isSaving, setIsSaving] = useState(false);
    const [isHiddenInput, setIsHiddenInput] = useState(isHidden || false);
    const [attachments, setAttachments] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [isSliderLoading, setIsSliderLoading] = useState(false);

    const initialTextValueRef = useRef(null);
    const initialHiddenStateRef = useRef(null);
    const initialImageNamesRef = useRef(null);
    const initialValuesSetRef = useRef(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [imageModalIndex, setImageModalIndex] = useState(0);
    const fileInputRef = useRef(null);
    const prevImagesKeyRef = useRef('');
    const attachmentsRef = useRef([]);
    const existingImagesRef = useRef([]);

    const stableImageBlobUrls = useMemo(() => existingImageBlobUrls, [existingImageBlobUrls]);
    const stableImageNames = useMemo(() => existingImageNames, [existingImageNames]);

    const isServiceInspection = resource ? resource === 'service-inspection' : globalThis.location.pathname.includes('/service-inspection');

    const handleTextInputClick = () => {
        setIsEditing(true);
    };

    const cleanupAttachments = attachmentsToClean => {
        attachmentsToClean.forEach(attachment => {
            if (attachment.previewUrl) {
                URL.revokeObjectURL(attachment.previewUrl);
            }
        });
    };

    const cleanupExistingImages = imagesToClean => {
        imagesToClean.forEach(image => {
            if (image.blobUrl?.startsWith('blob:')) {
                const isInCache = Array.from(commentImageBlobCache.values()).includes(image.blobUrl);
                if (!isInCache) {
                    URL.revokeObjectURL(image.blobUrl);
                }
            }
        });
    };

    const handleSave = useCallback(
        async formValues => {
            const htmlContent = formValues[source] || '';
            setIsSaving(true);

            try {
                const imagesPayload = attachments.map(attachment => ({
                    rawFile: attachment.file,
                    title: attachment.file.name,
                }));

                await dataProvider.post(`${resource}/${isServiceInspection ? serviceInspectionId : serviceRequestId}/change`, {
                    data: {
                        value: htmlContent,
                        hidden: isHiddenInput,
                        images: imagesPayload,
                    },
                });

                if (!isMountedRef.current) return;
                setIsEditing(false);
                setValue(source, '');
                refresh();
            } catch (error) {
                if (!isMountedRef.current) return;
                notify(error?.body?.message || translate('common.errorOccurred'), { type: 'error' });
            } finally {
                if (isMountedRef.current) {
                    setIsSaving(false);
                    setIsHiddenInput(false);
                    setAttachments(prev => {
                        cleanupAttachments(prev);
                        return [];
                    });
                }
            }
        },
        [
            source,
            serviceRequestId,
            serviceInspectionId,
            attachments,
            isHiddenInput,
            dataProvider,
            setValue,
            refresh,
            notify,
            translate,
            resource,
            isServiceInspection,
        ],
    );

    const handleSaveEdit = useCallback(
        async formValues => {
            const htmlContent = formValues[source] || '';

            setIsSaving(true);
            try {
                const newImagesPayload = attachments.map(attachment => ({
                    rawFile: attachment.file,
                    title: attachment.file.name,
                }));

                const existingImagesPayload = existingImages.map(image => {
                    const cachedData = commentImageBlobCache.get(image.name);
                    return {
                        data: cachedData ?? null,
                        name: image.name,
                        type: 'file',
                    };
                });

                const imagesPayload = [...existingImagesPayload, ...newImagesPayload];

                await dataProvider.put(`${resource}/${isServiceInspection ? serviceInspectionId : serviceRequestId}/change/${changeId}`, {
                    data: {
                        value: htmlContent,
                        hidden: isHiddenInput,
                        images: imagesPayload,
                    },
                });

                if (!isMountedRef.current) return;
                if (onEditSuccess) {
                    onEditSuccess();
                }

                setAttachments(prev => {
                    cleanupAttachments(prev);
                    return [];
                });
                setExistingImages([]);

                refresh();
            } catch (error) {
                if (!isMountedRef.current) return;
                notify(error?.body?.message || translate('common.errorOccurred'), { type: 'error' });
            } finally {
                if (isMountedRef.current) {
                    setIsSaving(false);
                }
            }
        },
        [
            source,
            serviceRequestId,
            serviceInspectionId,
            changeId,
            attachments,
            existingImages,
            isHiddenInput,
            dataProvider,
            onEditSuccess,
            refresh,
            notify,
            translate,
            resource,
            isServiceInspection,
        ],
    );

    const handleCancel = useCallback(() => {
        if (isEditingComment && onCancelEdit) {
            onCancelEdit();
            return;
        }
        setIsEditing(false);
    }, [isEditingComment, onCancelEdit]);

    const plainText = useMemo(() => getPlainTextFromHtml(activitiesValue || ''), [activitiesValue]);

    const isValidTextLength = useMemo(() => {
        const textWithoutWhitespace = plainText.replaceAll(/\s/g, '');
        return textWithoutWhitespace.length >= 3;
    }, [plainText]);

    useEffect(() => {
        setIsEditing(isEditingComment);
    }, [isEditingComment]);

    useEffect(() => {
        if (!autoOpen) return;
        setIsEditing(true);
        if (onAutoOpenHandled) {
            onAutoOpenHandled();
        }
    }, [autoOpen, onAutoOpenHandled]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (isEditingComment && !initialValuesSetRef.current) {
            initialTextValueRef.current = activitiesValue || '';
            initialHiddenStateRef.current = isHidden || false;
            initialImageNamesRef.current = [...existingImageNames];
            initialValuesSetRef.current = true;
        } else if (!isEditingComment) {
            initialValuesSetRef.current = false;
            initialTextValueRef.current = null;
            initialHiddenStateRef.current = null;
            initialImageNamesRef.current = null;
        }
    }, [isEditingComment, activitiesValue, isHidden, existingImageNames]);

    const hasChanges = useMemo(() => {
        if (!isEditingComment || !initialValuesSetRef.current) return false;

        const textChanged = (activitiesValue || '') !== (initialTextValueRef.current || '');
        const hiddenChanged = isHiddenInput !== initialHiddenStateRef.current;

        const currentImageNames = [...existingImages.map(img => img.name), ...attachments.map(att => att.file.name)];
        const initialImageNames = initialImageNamesRef.current || [];

        if (currentImageNames.length !== initialImageNames.length) {
            return textChanged || hiddenChanged || true;
        }

        const imagesChanged =
            !currentImageNames.every(name => initialImageNames.includes(name)) || !initialImageNames.every(name => currentImageNames.includes(name));

        return textChanged || hiddenChanged || imagesChanged;
    }, [isEditingComment, activitiesValue, isHiddenInput, existingImages, attachments]);

    const handleExistingImagesForEdit = (blobUrls, imageNames) => {
        const hasValidImages = blobUrls.length > 0 && imageNames.length > 0;
        if (!hasValidImages) {
            setExistingImages([]);
            return;
        }
        const images = blobUrls.map((blobUrl, index) => ({
            id: `existing-${index}`,
            name: imageNames[index],
            blobUrl,
            isExisting: true,
        }));
        setExistingImages(images);
    };

    const handleExistingImagesForNew = () => {
        setExistingImages([]);
    };

    useEffect(() => {
        const currentKey = `${isEditingComment}-${JSON.stringify(stableImageBlobUrls)}-${JSON.stringify(stableImageNames)}`;

        if (currentKey === prevImagesKeyRef.current) {
            return;
        }

        prevImagesKeyRef.current = currentKey;

        if (isEditingComment) {
            handleExistingImagesForEdit(stableImageBlobUrls, stableImageNames);
            return;
        }
        handleExistingImagesForNew();
    }, [isEditingComment, stableImageBlobUrls, stableImageNames]);

    useEffect(() => {
        const removed = attachmentsRef.current.filter(prevAtt => !attachments.some(att => att.id === prevAtt.id));
        cleanupAttachments(removed);
        attachmentsRef.current = attachments;
    }, [attachments]);

    useEffect(() => {
        const removed = existingImagesRef.current.filter(prevImg => !existingImages.some(img => img.name === prevImg.name));
        cleanupExistingImages(removed);
        existingImagesRef.current = existingImages;
    }, [existingImages]);

    useEffect(() => {
        if (!isEditing || !editorRef.current) return;
        const timeoutId = setTimeout(() => {
            if (!isMountedRef.current) return;
            const editor = editorRef.current?.querySelector('.ProseMirror');
            if (editor) {
                editor.focus();
            }
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [isEditing]);

    useEffect(() => {
        return () => {
            cleanupAttachments(attachmentsRef.current);
            cleanupExistingImages(existingImagesRef.current);
        };
    }, []);

    const handleCameraClick = useCallback(event => {
        event.preventDefault();
        event.stopPropagation();
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const getCurrentImageCountForEdit = useCallback(() => {
        return existingImages.length + attachments.length;
    }, [existingImages.length, attachments.length]);

    const getCurrentImageCountForNew = useCallback(() => {
        return attachments.length;
    }, [attachments.length]);

    const handleFilesChange = useCallback(
        event => {
            const files = Array.from(event.target.files || []);
            const currentCount = isEditingComment ? getCurrentImageCountForEdit() : getCurrentImageCountForNew();
            const { validFiles, shouldClear } = processFiles(files, currentCount, notify, translate);

            if (shouldClear) {
                event.target.value = '';
                return;
            }

            const newAttachments = validFiles.map(createAttachmentFromFile);
            setAttachments(prev => [...prev, ...newAttachments]);
            event.target.value = '';
        },
        [isEditingComment, getCurrentImageCountForEdit, getCurrentImageCountForNew, notify, translate],
    );

    const handleRemoveAttachment = id => {
        setAttachments(prev =>
            prev.filter(attachment => {
                if (attachment.id === id && attachment.previewUrl) {
                    URL.revokeObjectURL(attachment.previewUrl);
                }
                return attachment.id !== id;
            }),
        );
    };

    const handleRemoveExistingImage = id => {
        setExistingImages(prev => prev.filter(image => image.id !== id));
    };

    const handleImageRemove = id => {
        const isExisting = existingImages.some(img => img.id === id);
        if (isExisting) {
            handleRemoveExistingImage(id);
            return;
        }
        handleRemoveAttachment(id);
    };

    const getCombinedImagesForEdit = () => {
        return [...existingImages.map(img => ({ id: img.id, previewUrl: img.blobUrl, isExisting: true })), ...attachments];
    };

    const getCombinedImagesForNew = () => {
        return attachments;
    };

    const getCombinedBlobUrls = () => {
        if (isEditingComment) {
            const urls = [...existingImages.map(img => img.blobUrl), ...attachments.map(att => att.previewUrl)];
            return urls.reduce((acc, url, index) => {
                acc[index] = url;
                return acc;
            }, {});
        }
        return attachments.reduce((acc, att, index) => {
            acc[index] = att.previewUrl;
            return acc;
        }, {});
    };

    const getImagesDataForSlider = () => {
        if (isEditingComment && changeId) {
            const existingImagesData = existingImages.map(image => ({
                serviceRequestId: isServiceInspection ? null : serviceRequestId,
                serviceInspectionId: isServiceInspection ? serviceInspectionId : null,
                changeId,
                imageName: image.name,
            }));
            const newImagesData = attachments.map(attachment => ({
                serviceRequestId: isServiceInspection ? null : serviceRequestId,
                serviceInspectionId: isServiceInspection ? serviceInspectionId : null,
                changeId: `temp-${attachment.id}`,
                imageName: attachment.file.name,
            }));
            return [...existingImagesData, ...newImagesData];
        }

        return attachments.map(attachment => ({
            serviceRequestId: isServiceInspection ? null : serviceRequestId,
            serviceInspectionId: isServiceInspection ? serviceInspectionId : null,
            changeId: `temp-${attachment.id}`,
            imageName: attachment.file.name,
        }));
    };

    const handleImageClick = useCallback(index => {
        setImageModalIndex(index);
        setImageModalOpen(true);
    }, []);

    const renderEditingView = () => {
        const canToggleVisibility = isAdmin || isServiceMaster;
        const images = isEditingComment ? getCombinedImagesForEdit() : getCombinedImagesForNew();
        const onImageRemove = isEditingComment ? handleImageRemove : handleRemoveAttachment;
        const hasImages = images.length > 0;

        return (
            <Box sx={{ width: '100%', overflow: 'visible' }}>
                <Box
                    className='rich-text-input-wrapper'
                    ref={editorRef}
                    sx={{
                        width: 'calc(100% - 4px)',
                        paddingTop: '2px',
                        margin: 'auto',
                        position: 'relative',
                        overflow: 'visible',
                        '& .service-request-rich-text-input': {
                            '& .RaRichTextInput-editorContent .tiptap': {
                                minHeight: '254px',
                            },
                            '& button[title="Strikethrough"]': {
                                display: 'none',
                            },
                            '& button[title="Code"]': {
                                display: 'none',
                            },
                            '& .MuiFormHelperText-root': {
                                display: 'block',
                            },
                            '& .MuiToggleButtonGroup-root:nth-of-type(1)': {
                                position: 'absolute',
                                bottom: 48,
                                left: 24,
                                [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                                    bottom: 94,
                                },
                            },
                            '& .MuiToggleButtonGroup-root:nth-of-type(2)': {
                                position: 'absolute',
                                bottom: 48,
                                left: 156,
                                [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                                    bottom: isEditingComment ? 94 : 48,
                                    left: isEditingComment ? 156 : 24,
                                },
                            },
                            '& .tiptap': {
                                paddingRight: '54px !important',
                            },
                        },
                    }}
                >
                    {canToggleVisibility && (
                        <Tooltip
                            title={
                                isHiddenInput
                                    ? translate('resources.serviceRequest.activityList.showNote')
                                    : translate('resources.serviceRequest.activityList.hideNote')
                            }
                            arrow
                            classes={{ tooltip: 'tooltip-black tooltip-visibility' }}
                        >
                            <Box sx={activityStyles.commentVisibilityBox}>
                                <IconButton onClick={() => setIsHiddenInput(!isHiddenInput)}>{isHiddenInput ? <HiddenIcon /> : <ShowIcon />}</IconButton>
                            </Box>
                        </Tooltip>
                    )}
                    <CustomRichText
                        className='service-request-rich-text-input'
                        source={source}
                        hasImages={hasImages}
                        fullWidth
                        validate={validateActivitiesLength(translate)}
                    />
                    <input
                        type='file'
                        accept={IMAGE_CONSTANTS.ALLOWED_TYPES.join(',')}
                        multiple
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFilesChange}
                    />
                    <CommentImageThumbnailsContainer images={images} onImageRemove={onImageRemove} onImageClick={handleImageClick} />
                    <CommentImageSlider
                        open={imageModalOpen}
                        onClose={() => setImageModalOpen(false)}
                        images={getImagesDataForSlider()}
                        initialIndex={imageModalIndex}
                        serviceRequestId={isServiceInspection ? null : serviceRequestId}
                        serviceInspectionId={isServiceInspection ? serviceInspectionId : null}
                        blobUrls={getCombinedBlobUrls()}
                        resource={resource}
                        onLoadingChange={setIsSliderLoading}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 48,
                            right: 24,
                            zIndex: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                                bottom: 50,
                            },
                        }}
                    >
                        <IconButton
                            onClick={handleCameraClick}
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                backgroundColor: Palette.gray.gray100,
                                boxShadow: Palette.boxShadow?.elevation2 || '0px 1px 3px rgba(0,0,0,0.2)',
                                '&:hover': {
                                    backgroundColor: Palette.gray.gray150,
                                },
                            }}
                        >
                            <CameraIcon />
                        </IconButton>
                        {isEditingComment ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '10px',
                                    '& .MuiButton-root': {
                                        padding: '8px 16px',
                                    },
                                }}
                            >
                                <Button size='small' variant='text' color='secondary' onClick={handleCancel}>
                                    {translate('common.cancel')}
                                </Button>
                                <Button
                                    size='small'
                                    variant='contained'
                                    type='submit'
                                    disabled={isSaving || isSliderLoading || !hasChanges || !isValidTextLength}
                                    onClick={handleSubmit(handleSaveEdit)}
                                >
                                    {translate('common.save')}
                                </Button>
                            </Box>
                        ) : (
                            <SaveButton
                                label={translate('common.buttons.send')}
                                icon={null}
                                disabled={isSaving || isSliderLoading || !isValidTextLength}
                                sx={{
                                    minWidth: '79px',
                                    padding: '6px 10px',
                                    borderRadius: '12px',
                                }}
                                onClick={handleSubmit(handleSave)}
                            />
                        )}
                    </Box>
                </Box>
                {isSaving && (
                    <Box sx={{ margin: '0px auto 10px' }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
            </Box>
        );
    };

    const renderNonEditingView = () => {
        return (
            <Box onClick={handleTextInputClick} sx={{ cursor: 'pointer', position: 'relative', width: '100%' }}>
                <TextInput
                    source={source}
                    label={translate('resources.serviceRequest.labels.message') || 'Wiadomość'}
                    fullWidth
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment
                                position='end'
                                sx={{
                                    '& .MuiButtonBase-root.MuiButton-root': {
                                        height: '36px',
                                        padding: '6px 15px',
                                    },
                                    '& .MuiButtonBase-root.MuiIconButton-root': {
                                        marginRight: '0',
                                        backgroundColor: Palette.gray.gray300,
                                        '& svg': {
                                            '& path': {
                                                stroke: Palette.gray.gray400,
                                            },
                                        },
                                    },
                                }}
                            >
                                {isDesktop ? (
                                    <CustomButton
                                        variant='contained'
                                        color='primary'
                                        disabled
                                        label={translate('common.buttons.send')}
                                        onClick={handleTextInputClick}
                                        sx={{
                                            borderRadius: '10px',
                                        }}
                                    />
                                ) : (
                                    <IconButton disabled edge='end'>
                                        <ArrowRightSmallIcon />
                                    </IconButton>
                                )}
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        margin: '0',
                        '& .MuiInputBase-root': {
                            borderRadius: '16px',
                            cursor: 'pointer',
                            pointerEvents: 'none',
                            padding: '22px 16px !important',
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                padding: '20px 16px !important',
                            },
                        },
                        '& .MuiFormLabel-root.MuiInputLabel-root': {
                            top: '10px',
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                top: '8px',
                            },
                        },
                        '& .MuiInputBase-input': {
                            cursor: 'pointer',
                        },
                        '& input': {
                            color: plainText ? 'inherit' : Palette.gray.gray400,
                        },
                    }}
                    format={() => plainText}
                />
            </Box>
        );
    };

    if (isEditing) {
        return renderEditingView();
    }

    return renderNonEditingView();
};

CommentInput.propTypes = {
    source: PropTypes.string.isRequired,
    serviceRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    serviceInspectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isEditingComment: PropTypes.bool,
    changeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCancelEdit: PropTypes.func,
    onEditSuccess: PropTypes.func,
    existingImageBlobUrls: PropTypes.arrayOf(PropTypes.string),
    existingImageNames: PropTypes.arrayOf(PropTypes.string),
    isHidden: PropTypes.bool,
    resource: PropTypes.string.isRequired,
    autoOpen: PropTypes.bool,
    onAutoOpenHandled: PropTypes.func,
};

export default CommentInput;
